import { getShareCalculationParams } from '@pages/AdditionalServices/tabs/DeliveryForm/helpers';
import { getCachedShortLinkData } from '@pages/History/helpers';
import {
  HistoryOrderCargo,
  HistoryRouteData,
  HistoryRouteParams,
  HistoryRoutePointPosition,
  HistoryShareLocation,
} from '@pages/History/types';
import { locationsBranchService } from '@services/requests/routeCalculator/locationsBranchSearchService';
import { BranchItem } from '@services/requests/routeCalculator/locationsBranchSearchService/interfaces';
import { terminalByLocationLoader } from '@services/requests/routeCalculator/TerminalByLocationLoader';
import { shortLinkService } from '@services/requests/shortLinkService';
import { TFunction } from 'i18next';

/**
 * Полная локация с локализованными названиями
 */
const loadLocalizedLocation = async (
  location: HistoryShareLocation
): Promise<BranchItem | undefined> => {
  if (!location?.id) {
    return undefined;
  }

  try {
    const locationIds = [location.id, location.parentId].filter((id) => !!id);
    const locations = await terminalByLocationLoader().Load(locationIds, [location.id]);
    const localizedLocations = await locationsBranchService().loadLocalization(locations);

    return (
      localizedLocations.find((l) => l.id === location.id && l.type === location.type) ??
      localizedLocations.find((l) => l.id === location.id)
    );
  } catch (e) {
    return undefined;
  }
};

/**
 * Параметры расчета доставки по короткой ссылке
 */
export const loadHistoryRouteData = (shortLink: string): Promise<HistoryRouteData> => {
  const loadRouteData = async (): Promise<HistoryRouteData> => {
    const shortLinkData = await shortLinkService().LoadShortLink({ id: shortLink });

    if (!shortLinkData?.data) {
      throw new Error(`Short link data not found: ${shortLink}`);
    }

    const [isValid, parsedParams] = getShareCalculationParams(JSON.parse(shortLinkData.data));

    if (!isValid) {
      throw new Error(`Short link data is invalid: ${shortLink}`);
    }

    const [startLocation, endLocation] = await Promise.all([
      loadLocalizedLocation(parsedParams.startLocation),
      loadLocalizedLocation(parsedParams.endLocation),
    ]);

    return { params: parsedParams, startLocation, endLocation };
  };

  return getCachedShortLinkData(shortLink, loadRouteData);
};

/**
 * Параметры груза для выбранного типа
 */
const getActiveCargoParams = (params: HistoryRouteParams) => {
  switch (params.cargoType) {
    case 'container':
      return params.containerParameters;
    case 'auto':
      return params.truckParameters;
    default:
      return params.customCargoParameters;
  }
};

/**
 * Название локации как в LocationSelector, но без типа точки
 */
const getLocalizedLocationName = (
  location: BranchItem | undefined,
  shareLocation: HistoryShareLocation,
  langId: string
) => {
  if (!location) {
    return shareLocation?.visibleName ?? '';
  }

  if (location.main) {
    return (
      location.localizedNamesArray?.find((i) => i.lang_id === langId)?.message ||
      location.defaultName ||
      location.visibleName
    );
  }

  return (
    location.localizedNamesArray?.find((i) => i.lang_id === langId)?.message ||
    location.visibleName
  );
};

/**
 * Подпись точки маршрута с условиями погрузки/разгрузки
 */
export const getLocationLabel = (
  data: HistoryRouteData,
  position: HistoryRoutePointPosition,
  langId: string,
  t: TFunction
) => {
  const { params } = data;
  const isStart = position === 'start';
  const cargoParams = getActiveCargoParams(params);

  const conditionId = String(
    (isStart
      ? cargoParams?.startTransportingConditionId ?? params.startTransportingCondition
      : cargoParams?.endTransportingConditionId ?? params.endTransportingCondition) ?? ''
  );

  const name = getLocalizedLocationName(
    isStart ? data.startLocation : data.endLocation,
    isStart ? params.startLocation : params.endLocation,
    langId
  );

  switch (conditionId) {
    case '1':
      return `${name} (${t('Common:Terminal')}, ${t(
        isStart ? 'OrderList:WithoutLoading' : 'OrderList:WithoutUnloading'
      )})`;
    case '2':
      return `${name} (${t('Common:Terminal')}, ${t(
        isStart ? 'OrderList:WithLoading' : 'OrderList:WithUnloading'
      )})`;
    case '3':
      return `${name} (${t('Common:Warehouse')})`;
    default:
      return name;
  }
};

/**
 * Груз из формы калькулятора в формате getCargo из корзины
 */
export const mapShareCargoToOrderCargo = (params: HistoryRouteParams): HistoryOrderCargo => {
  let cargoType: HistoryOrderCargo['cargoType'] = 'custom';

  if (params.cargoType === 'container') {
    cargoType = 'container';
  }

  if (params.cargoType === 'auto') {
    cargoType = 'truck';
  }

  const containers: HistoryOrderCargo['cargoParams']['containers'] = (
    params.containerParameters?.containerParameters ?? []
  ).map((container, index) => ({
    identifier: String(index),
    containerId: container.containerId ?? '',
    eachContainerWeight: Number(container.eachContainerWeight),
    containerQuantity: Number(container.containerQuantity),
    withPallets: container.withPallets ?? false,
    palletsLoaded: container.palletsLoaded ?? [],
  }));

  const trucks: HistoryOrderCargo['cargoParams']['trucks'] = (
    params.truckParameters?.truckParameters ?? []
  ).map((truck, index) => ({
    identifier: String(index),
    truckId: truck.truckId ?? '',
    eachTruckWeight: Number(truck.eachTruckWeight),
    truckQuantity: Number(truck.truckQuantity),
    withPallets: truck.withPallets ?? false,
    palletsLoaded: truck.palletsLoaded ?? [],
  }));

  const customs: HistoryOrderCargo['cargoParams']['customs'] = (
    params.customCargoParameters?.customCargoParameters ?? []
  ).map(({ parametersType, baseParameters, palletParameters, volumeParameters }, index) => ({
    identifier: String(index),
    customCargoType: parametersType,
    baseParameters: {
      height: Number(baseParameters?.height),
      length: Number(baseParameters?.length),
      width: Number(baseParameters?.width),
      weight: Number(baseParameters?.weight),
      quantity: Number(baseParameters?.quantity),
    },
    palletParameters: {
      normativeHeightOfStacking: Number(palletParameters?.normativeHeightOfStacking),
      type: palletParameters?.type,
      quantity: Number(palletParameters?.quantity),
      weight: Number(palletParameters?.weight),
    },
    volumeParameters: {
      normativeHeightOfStacking: Number(volumeParameters?.height),
      volume: Number(volumeParameters?.volume),
      weight: Number(volumeParameters?.weight),
    },
  }));

  return {
    cargoType,
    cargoParams: { containers, customs, trucks },
  };
};
